import React, { useEffect, useState, createContext, useContext } from 'react';
import googleDriveService from '../services/googleDrive';

// Create Auth Context
const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSigningIn, setIsSigningIn] = useState(false);
  
  // Register auth error handler with googleDriveService
  React.useEffect(() => {
    googleDriveService.setAuthErrorCallback((errorMessage) => {
      console.log('🔄 Auth error received from Drive service:', errorMessage);
      // Clear user state and show error
      setUser(null);
      alert(errorMessage);
    });
  }, []);

  useEffect(() => {
    // Check for persisted login state - ALWAYS restore if exists
    const savedUser = localStorage.getItem('drillDocs_user');
    const savedToken = localStorage.getItem('drillDocs_token');
    
    if (savedUser && savedToken) {
      console.log('🔄 Restoring previous session...');
      setUser(JSON.parse(savedUser));
      googleDriveService.accessToken = savedToken;
      
      // Don't check expiry - let the token work as long as possible
      const tokenTimestamp = localStorage.getItem('drillDocs_token_timestamp');
      if (tokenTimestamp) {
        const now = Date.now();
        const tokenAge = now - parseInt(tokenTimestamp);
        const daysOld = Math.round(tokenAge / (24 * 60 * 60 * 1000));
        console.log(`Token is ${daysOld} days old - keeping it active`);
      }
    }
    
    initializeGoogleServices();
  }, []);

  const clearExpiredSession = () => {
    localStorage.removeItem('drillDocs_user');
    localStorage.removeItem('drillDocs_token');
    localStorage.removeItem('drillDocs_token_timestamp');
    localStorage.removeItem('drillDocs_token_expiry');
    sessionStorage.removeItem('drillDocs_scopeChecked');
    googleDriveService.accessToken = null;
  };

  const initializeGoogleServices = async () => {
    try {
      console.log('🔄 Initializing Google services...');
      
      // Load Google Identity Services if not already loaded
      if (!window.google) {
        await loadGoogleScripts();
      }

      // Wait a bit more for APIs to be ready
      await new Promise(resolve => setTimeout(resolve, 1000));

      await googleDriveService.initialize();
      console.log('✅ Google services initialized');
      setIsLoading(false);
    } catch (error) {
      console.error('❌ Failed to initialize Google services:', error);
      setIsLoading(false);
    }
  };

  const loadGoogleScripts = () => {
    return new Promise((resolve, reject) => {
      const script1 = document.createElement('script');
      script1.src = 'https://accounts.google.com/gsi/client';
      script1.async = true;
      script1.defer = true;

      const script2 = document.createElement('script');
      script2.src = 'https://apis.google.com/js/api.js';
      script2.async = true;
      script2.defer = true;

      let scriptsLoaded = 0;
      const onScriptLoad = () => {
        scriptsLoaded++;
        if (scriptsLoaded === 2) {
          // Wait a bit for Google APIs to initialize
          setTimeout(resolve, 500);
        }
      };

      script1.onload = onScriptLoad;
      script2.onload = onScriptLoad;
      script1.onerror = script2.onerror = reject;

      document.head.appendChild(script1);
      document.head.appendChild(script2);
    });
  };

  const handleSignIn = async () => {
    if (isSigningIn) return;
    
    try {
      setIsSigningIn(true);
      console.log('🔑 Starting sign-in process...');
      
      // Get Drive access token (this now handles extended sessions)
      await googleDriveService.getAccessToken();
      
      // Set user as signed in with extended session info
      const now = new Date();
      const expiresAt = new Date(now.getTime() + (30 * 24 * 60 * 60 * 1000)); // 30 days from now
      
      const userData = { 
        name: 'DrillDocs User', 
        email: 'user@drilldocs.com',
        signedInAt: now.toISOString(),
        sessionExpiresAt: expiresAt.toISOString(),
        sessionDuration: '30 days'
      };
      
      setUser(userData);
      
      // Persist login state (token and timestamp are already saved by googleDriveService)
      localStorage.setItem('drillDocs_user', JSON.stringify(userData));
      
      console.log('✅ Sign-in successful with 30-day session!', {
        signedIn: now.toLocaleString(),
        expires: expiresAt.toLocaleString()
      });
    } catch (error) {
      console.error('❌ Sign-in failed:', error);
      alert(`Sign-in failed: ${error.message}`);
    } finally {
      setIsSigningIn(false);
    }
  };

  const handleSignOut = () => {
    setUser(null);
    googleDriveService.accessToken = null;
    
    // Clear all persisted state
    clearExpiredSession();
    
    console.log('👋 User signed out and session cleared');
  };

  const forceReauth = async () => {
    console.log('🔄 Forcing re-authentication with new scopes...');
    handleSignOut(); // Clear current session
    setIsSigningIn(true);
    
    try {
      // Force new authentication with expanded scopes
      await googleDriveService.getAccessToken(true);
      
      const userData = { 
        name: 'DrillDocs User', 
        email: 'user@drilldocs.com',
        signedInAt: new Date().toISOString()
      };
      
      setUser(userData);
      localStorage.setItem('drillDocs_user', JSON.stringify(userData));
      // Token is already stored by googleDriveService
      
      console.log('✅ Re-authentication successful with expanded scopes!');
      window.location.reload(); // Refresh to apply new permissions
    } catch (error) {
      console.error('❌ Re-authentication failed:', error);
      alert('Please sign in again to enable document creation features.');
    } finally {
      setIsSigningIn(false);
    }
  };

  // DISABLED auto scope checking to prevent unnecessary re-authentication
  // The token should work for 30 days without any validation

  return (
    <AuthContext.Provider value={{ 
      user, 
      isLoading, 
      isSigningIn, 
      signIn: handleSignIn, 
      signOut: handleSignOut 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

const Auth = () => {
  const { user, isLoading, isSigningIn, signIn, signOut } = useAuth();

  if (isLoading) {
    return (
      <div className="auth-container">
        <div className="auth-loading">
          <div className="spinner" style={{ width: '16px', height: '16px' }}></div>
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  // Calculate remaining session time
  const getSessionInfo = () => {
    if (!user || !user.sessionExpiresAt) return null;
    
    const now = new Date();
    const expires = new Date(user.sessionExpiresAt);
    const timeLeft = expires - now;
    
    if (timeLeft <= 0) return { expired: true };
    
    const daysLeft = Math.ceil(timeLeft / (24 * 60 * 60 * 1000));
    return { daysLeft, expired: false };
  };

  const sessionInfo = getSessionInfo();

  return (
    <div className="auth-container">
      {user ? (
        <div className="auth-user">
          <span>Welcome, {user.name}</span>
          {sessionInfo && !sessionInfo.expired && (
            <span className="auth-session-info">
              ({sessionInfo.daysLeft} days left)
            </span>
          )}
          <button onClick={signOut} className="auth-button">
            Sign Out
          </button>
        </div>
      ) : (
        <div className="auth-signin">
          <button 
            onClick={signIn} 
            className="auth-button"
            disabled={isSigningIn}
          >
            {isSigningIn ? 'Signing in...' : 'Sign in with Google'}
          </button>
        </div>
      )}
    </div>
  );
};

export default Auth;